import { Navigate, useParams } from "react-router";

import { useAuth } from "@/providers";
import { LoadingScreen, PageLayout } from "@/shared/components";
import { useAllDevicesByOrg } from "@/shared/domain/devices/hooks/useAllDevicesByOrg";
import { Banner } from "@/shared/foundation/primitives";
import { Goto } from "@/shared/navigation";

/**
 * Opens the Configurations tab of the device whose `dv_` ID is in the URL, at
 * the folder the rest of the URL names. A device's files identify the device
 * only by that ID, but the device page's URL uses the device ID the customer
 * chose, so this looks the device up and redirects.
 */
export function DeviceConfigurationsRedirect() {
  const { universalDeviceId, "*": directoryPath } = useParams();
  const { currentOrganization } = useAuth();
  // The devices API cannot look a device up by its `dv_` ID, so this lists
  // every device in the org and searches the list. The list is asked for
  // afresh, since a cached one may predate the device the link names.
  const devicesQuery = useAllDevicesByOrg(currentOrganization?.org_id, {
    staleTime: 0,
  });

  const device = devicesQuery.data?.find(
    (candidate) => candidate.universal_device_id === universalDeviceId,
  );

  // A cached list that lacks the device is not an answer until the fresh one
  // arrives.
  if (devicesQuery.isPending || (!device && devicesQuery.isFetching)) {
    return <LoadingScreen />;
  }

  if (!device) {
    return (
      <PageLayout title="Roboto - Device" headerContentLeft="Device">
        <Banner variant="error">
          {devicesQuery.isError
            ? "The organization's devices could not be loaded."
            : `No device in this organization has the ID ${universalDeviceId}.`}
        </Banner>
      </PageLayout>
    );
  }

  return (
    <Navigate
      to={Goto.createDeviceConfigurationsRoute(device.device_id, directoryPath)}
      replace
    />
  );
}
